<template>
	<Dialog
		:dialog-visible="showSupportDialog"
		:title="'Contact Support'"
		:message="message"
		:options="{ width: '560px' }"
		:agree-text="sendingSupportRequest ? 'Sending' : 'Send'"
		:cancel-text="'Cancel'"
		:primary-cancel-button="!isAuthenticated"
		:disable-agree-button="sendingSupportRequest"
		:remove-agree-button="!isAuthenticated"
		@agree="submitSupportRequest"
		@cancel="close">
		<template #dialog-body>
			<div v-if="isAuthenticated" class="mt-4">
				<div class="text-body-2 font-weight-medium mb-2">What do you need help with?</div>
				<v-radio-group v-model="selectedTopic" hide-details density="comfortable" class="support-topic-group mt-0">
					<v-radio
						v-for="opt in SUPPORT_OPTIONS"
						:key="opt.id"
						v-test="`contact-support-topic-${opt.id}`"
						:value="opt.id"
						color="primary"
						class="support-topic-radio">
						<template #label>
							<div class="support-topic-label">
								<span v-test="`contact-support-label-${opt.id}`" class="text-body-2">{{ opt.label }}</span>
								<v-spacer class="d-none d-sm-flex" />
								<a
									v-test="`contact-support-email-${opt.id}`"
									:href="`mailto:${opt.email}`"
									target="_blank"
									class="text-decoration-none text-body-2 support-topic-email"
									@click.stop>
									{{ opt.email }}
								</a>
							</div>
						</template>
					</v-radio>
				</v-radio-group>

				<div class="mt-3 text-body-2 text-medium-emphasis">
					Or call us:
					<a href="tel:+1 844-587-9824" target="_blank" class="text-decoration-none">+1 844-587-9824</a>
				</div>

				<v-form ref="form">
					<CharacterLimitedTextArea
						id="support-dialog-message"
						v-model.trim="supportDialogContent"
						field-noun="message"
						v-test="'contact-support-message'"
						class="support-message mt-4"
						rows="5"
						:disabled="sendingSupportRequest"
						:max-length="MESSAGE_MAX_LENGTH"
						:rules="messageRules"
						validate-on="invalid-input"
						enterkeyhint="enter"
						@keydown.enter.stop>
						<template #label><FieldLabel label="Message (Required)" /></template>
					</CharacterLimitedTextArea>
				</v-form>
			</div>
			<div v-else class="mt-4">
				<div class="text-body-2 mb-3">You can reach us through the emails and phone number below.</div>
				<div v-for="opt in SUPPORT_OPTIONS" :key="opt.id" class="d-flex flex-column flex-sm-row align-sm-center ga-1 ga-sm-2 mb-2">
					<span class="text-body-2">{{ opt.label }}</span>
					<v-spacer class="d-none d-sm-flex" />
					<a
						:href="`mailto:${opt.email}`"
						target="_blank"
						class="text-decoration-none text-body-2 support-topic-email align-self-end align-self-sm-auto">
						{{ opt.email }}
					</a>
				</div>
				<div class="text-body-2 text-medium-emphasis mt-3">
					Phone:
					<a href="tel:+1 844-587-9824" target="_blank" class="text-decoration-none">+1 844-587-9824</a>
				</div>
			</div>
		</template>
	</Dialog>
</template>
<script lang="ts" setup>
	import { ref, onMounted, computed } from "vue";
	import Dialog from "./Dialog.vue";
	import api from "../api";
	import { getMessageFromError, calculateTimeout } from "@/helpers/utilities";
	import { VuetifyValidation } from "@/helpers/Validation";
	import { useToast } from "vue-toastification";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	type SupportTopicId = "help" | "accounts";

	const SUPPORT_OPTIONS: ReadonlyArray<{ id: SupportTopicId; label: string; email: string }> = [
		{ id: "help", label: "For support, questions, or consulting:", email: "help@liveswitch.com" },
		{ id: "accounts", label: "For billing or account management:", email: "accounts@liveswitch.com" }
	];

	const showSupportDialog = ref(false);
	const supportDialogContent = ref("");
	const sendingSupportRequest = ref(false);
	const form = ref<any>(null);
	const toast = useToast();
	const MESSAGE_MAX_LENGTH = 2000;
	// MESSAGE_MAX_LENGTH (2000 code points) is now enforced by CharacterLimitedTextArea's :max-length
	// (LSP-16852). No server-unit backstop needed: contact-api's POST /me/support (routes/me.ts) only
	// checks presence (`message.trim().length === 0`) — the message is forwarded to email/HubSpot, not
	// stored in a length-bound column, so there is no narrower server threshold to guard against.
	const messageRules = [VuetifyValidation.RequiredValidator("Message").isValid];
	const isAuthenticated = ref(false);
	const selectedTopic = ref<SupportTopicId>("help");

	// LSP-19786: plain, conversational copy. The old wording ("the best phone number at which to
	// reach you") read like a form letter; the phone-number request itself has to stay, because it is
	// the only way support can call the customer back.
	const message = computed(() => {
		if (isAuthenticated.value) {
			return "Need help? We've got you. Choose what you need help with, tell us a little more about what's going on, and leave the best phone number to reach you.";
		}
		return "Need help? We've got you.";
	});

	defineExpose({
		open,
		submitSupportRequest
	});

	onMounted(async () => {
		try {
			await api.getMe();
			isAuthenticated.value = true;
		} catch (e) {
			console.warn("error", e);
		}
	});

	function open() {
		showSupportDialog.value = true;
		supportDialogContent.value = "";
		selectedTopic.value = "help";
		reset();
	}

	async function submitSupportRequest() {
		try {
			const validation = await form.value?.validate();
			if (!validation?.valid || !isAuthenticated.value) {
				return;
			}

			sendingSupportRequest.value = true;
			await api.submitSupportRequest(supportDialogContent.value, selectedTopic.value);
			const toastSuccessMessage =
				"Your support request has been submitted, and someone from our team will get back to you right away.";
			toast.success(toastSuccessMessage, { timeout: calculateTimeout(toastSuccessMessage) });
			close();
		} catch (e: any) {
			const errorMessage = getMessageFromError(e);
			toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
			console.error(e, {
				tags: {
					method: "submitSupportRequest",
					file: "ContactSupportDialog"
				}
			});
		} finally {
			sendingSupportRequest.value = false;
		}
	}

	function close() {
		showSupportDialog.value = false;
		reset();
	}

	function reset() {
		supportDialogContent.value = "";
		sendingSupportRequest.value = false;
		if (form.value) {
			form.value.resetValidation();
		}
	}
</script>

<style scoped>
	.support-topic-group {
		border: 1px solid rgba(0, 0, 0, 0.12);
		border-radius: 10px;
		overflow: hidden;
	}
	.support-topic-group :deep(.v-selection-control) {
		min-height: 36px;
		padding: 8px 12px;
		border-bottom: 1px solid rgba(0, 0, 0, 0.08);
	}
	.support-topic-group :deep(.v-selection-control:last-child) {
		border-bottom: none;
	}
	.support-topic-group :deep(.v-label) {
		flex: 1 1 auto;
		width: auto;
		min-width: 0;
		overflow-wrap: anywhere;
		opacity: 1;
	}
	.support-topic-label {
		display: flex;
		flex-direction: column;
		gap: 4px;
		width: 100%;
		min-width: 0;
		flex-wrap: wrap;
	}
	.support-topic-email {
		align-self: flex-end;
		word-break: break-word;
	}
	.support-message :deep(textarea.v-field__input) {
		margin-right: 0.25rem;
		position: relative;
		bottom: 0.25rem;
	}
	@media (min-width: 600px) {
		.support-topic-label {
			flex-direction: row;
			align-items: center;
			gap: 12px;
		}
		.support-topic-email {
			align-self: auto;
		}
	}
</style>
